import { money, fmtDate } from "../quoteUtils";

// Side-by-side comparison of two quote versions; changed rows are highlighted.
// Used by staff (showInternal) and customers.
const num = (v) => Number(v) || 0;
const text = (v) => (v == null || v === "" ? "" : String(v));

function rowsFor(before, after, showInternal) {
  const rows = [];
  const partsA = before?.parts || [];
  const partsB = after?.parts || [];
  const n = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < n; i++) {
    const a = partsA[i];
    const b = partsB[i];
    const title = (b?.name || a?.name || `Part ${i + 1}`) + (n > 1 ? ` (#${i + 1})` : "");
    rows.push({ group: title });
    if (!a || !b) {
      rows.push({ label: "Part", a: a ? a.name || "Part" : "Not included", b: b ? b.name || "Part" : "Removed", changed: true });
      if (!b) continue;
    }
    const fields = [
      ["Part name", text(a?.name), text(b?.name)],
      ["Part number", text(a?.partNumber), text(b?.partNumber)],
      ["Condition", text(a?.condition), text(b?.condition)],
      ["Vehicle", text(a?.vehicle), text(b?.vehicle)],
      ["KM", text(a?.km), text(b?.km)],
      ["Quantity", num(a?.quantity || 1), num(b?.quantity || 1)],
      ["Unit price", num(a?.unitPrice), num(b?.unitPrice), true],
      ["Freight", num(a?.freight), num(b?.freight), true],
      ["Core charge", num(a?.coreCharge), num(b?.coreCharge), true],
    ];
    fields.forEach(([label, va, vb, isMoney]) => {
      if (!isMoney && va === "" && vb === "") return;
      rows.push({ label, a: isMoney ? money(va) : va || "—", b: isMoney ? money(vb) : vb || "—", changed: va !== vb, delta: isMoney ? vb - va : null });
    });
  }

  const pa = before?.pricing || {};
  const pb = after?.pricing || {};
  rows.push({ group: "Totals" });
  [["Parts", "subtotal"], ["Freight", "freightTotal"], ["Core charge", "coreChargeTotal"], ["Total (AUD)", "total"]]
    .forEach(([label, k]) => rows.push({ label, a: money(pa[k]), b: money(pb[k]), changed: num(pa[k]) !== num(pb[k]), delta: num(pb[k]) - num(pa[k]), strong: k === "total" }));

  rows.push({ group: "Terms" });
  [
    ["Warranty", text(before?.warranty), text(after?.warranty)],
    ["Delivery", text(before?.deliveryType), text(after?.deliveryType)],
    ["Est. delivery", before?.estDelivery ? fmtDate(before.estDelivery) : "", after?.estDelivery ? fmtDate(after.estDelivery) : ""],
    ["Note to customer", text(before?.customerNote), text(after?.customerNote)],
    ["Attachments", String((before?.attachments || []).length), String((after?.attachments || []).length)],
    ...(showInternal ? [["Internal note", text(before?.internalNote), text(after?.internalNote)]] : []),
  ].forEach(([label, va, vb]) => {
    if (va === "" && vb === "") return;
    rows.push({ label, a: va || "—", b: vb || "—", changed: va !== vb });
  });
  return rows;
}

export default function QuoteCompare({ before, after, beforeLabel = "Before", afterLabel = "After", showInternal = false, onClose }) {
  const rows = rowsFor(before, after, showInternal);
  const changes = rows.filter((r) => r.changed);
  const totalDelta = num(after?.pricing?.total) - num(before?.pricing?.total);

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100">
        <div>
          <div className="font-bold text-slate-800 text-sm">What changed: {beforeLabel} → {afterLabel}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {changes.length === 0 ? "No differences." : `${changes.length} change${changes.length === 1 ? "" : "s"}`}
            {totalDelta !== 0 && (
              <> · total {totalDelta < 0 ? "down" : "up"} <b className={totalDelta < 0 ? "text-green-700" : "text-red-600"}>{money(Math.abs(totalDelta))}</b></>
            )}
          </div>
        </div>
        {onClose && <button onClick={onClose} className="text-xs font-semibold text-slate-500 hover:text-slate-800">Close</button>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase text-slate-400">
              <th className="px-4 py-2 font-medium w-1/4" />
              <th className="px-4 py-2 font-medium">{beforeLabel}</th>
              <th className="px-4 py-2 font-medium">{afterLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) =>
              r.group ? (
                <tr key={i}><td colSpan={3} className="px-4 pt-3 pb-1 text-xs font-bold text-slate-700">{r.group}</td></tr>
              ) : (
                <tr key={i} className={r.changed ? "bg-amber-50" : ""}>
                  <td className="px-4 py-1.5 text-slate-500 align-top">{r.label}</td>
                  <td className={`px-4 py-1.5 align-top whitespace-pre-line ${r.changed ? "text-slate-400 line-through decoration-slate-300" : "text-slate-600"}`}>{r.a}</td>
                  <td className={`px-4 py-1.5 align-top whitespace-pre-line ${r.changed ? "font-semibold text-slate-900" : "text-slate-600"} ${r.strong ? "font-bold" : ""}`}>
                    {r.b}
                    {r.changed && r.delta != null && r.delta !== 0 && (
                      <span className={`ml-2 text-xs font-semibold ${r.delta < 0 ? "text-green-700" : "text-red-600"}`}>
                        {r.delta < 0 ? "−" : "+"}{money(Math.abs(r.delta))}
                      </span>
                    )}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
